import { Plus } from '@deepup/icons';
import { useOrderNumbers } from '@elements/components/ElementDialog/useOrderNumbers';
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  OrderNumber,
  OrderNumberType,
  OrderNumberSubType,
  mapNetworkNodeTypeToOrderNumberSubType,
  isOrderNumberClosed,
} from '@projects/types/orderNumbers';
import React, { useState } from 'react';

interface AddSMADialogProps {
  open: boolean;
  onClose: () => void;
  onAddSMA: (sma: OrderNumber) => void;
  selectedSMAs: OrderNumber[];
  onRemoveSelectedSMA: (sma: OrderNumber) => void;
  entityId?: string;
}

type SMAOption = {
  name: string;
  smaNumber: string;
  subType?: OrderNumberSubType;
  isClosed?: boolean;
};

const AddSMADialog: React.FC<AddSMADialogProps> = ({
  open,
  onClose,
  onAddSMA,
  selectedSMAs,
  onRemoveSelectedSMA,
}) => {
  const { orderNumbers } = useOrderNumbers();
  const [selectedSMA, setSelectedSMA] = useState<SMAOption | null>(null);
  const [textInput, setTextInput] = useState<string>('');

  const smaOptions: SMAOption[] =
    orderNumbers?.flatMap((sma) => {
      const closed = isOrderNumberClosed(sma);
      const mapped =
        sma.networkNodeMappings?.map((mapping) => ({
          name: mapping.name,
          smaNumber: sma.id,
          subType: mapNetworkNodeTypeToOrderNumberSubType(mapping.type),
          isClosed: closed,
        })) || [];
      return mapped.length > 0 ? mapped : [{ name: sma.id, smaNumber: sma.id, isClosed: closed }];
    }) || [];

  const handleAdd = () => {
    if (!selectedSMA) return;
    const newSMA: OrderNumber = {
      type: OrderNumberType.SMA,
      value: selectedSMA.smaNumber,
      subType: selectedSMA.subType,
    };
    onAddSMA(newSMA);
    setSelectedSMA(null);
  };

  const handleSave = () => {
    setSelectedSMA(null);
    setTextInput('');
    onClose();
  };

  const handleCancel = () => {
    // cleanup pending SMA on cancel
    selectedSMAs.forEach((sma) => onRemoveSelectedSMA(sma));
    setSelectedSMA(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        elevation: 11,
        style: {
          width: '360px',
        },
      }}
    >
      <DialogTitle>Add SMAs to Asset</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <FormControl fullWidth>
            <Autocomplete<SMAOption>
              value={selectedSMA}
              inputValue={textInput}
              onInputChange={(event, newInputValue) => {
                setTextInput(newInputValue);
              }}
              onChange={(event, newValue) => {
                setSelectedSMA(newValue ?? null);
              }}
              options={smaOptions}
              getOptionDisabled={(option) => Boolean(option?.isClosed)}
              isOptionEqualToValue={(option, value) => option?.smaNumber === value?.smaNumber}
              getOptionLabel={(option) =>
                `${option.name} - ${option.smaNumber}${option.isClosed ? ' (Closed)' : ''}`
              }
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} label="SMA" placeholder="Search for SMA..." />
              )}
              renderOption={(props, option, i) => (
                <ListItem
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  key={`${option?.name}-${option?.smaNumber}-${i.index}`}
                  disableGutters
                  data-testid={`sma-option-${option?.smaNumber}`}
                  sx={{
                    ...(option?.isClosed && {
                      color: 'text.disabled',
                      opacity: 0.6,
                      backgroundColor: 'action.hover',
                    }),
                  }}
                >
                  <ListItemText>
                    {option?.name} {option?.isClosed ? '(Closed)' : ''}
                  </ListItemText>
                  <Typography
                    variant="body2"
                    color={option?.isClosed ? 'text.disabled' : 'secondary'}
                  >
                    {option?.smaNumber}
                  </Typography>
                </ListItem>
              )}
              freeSolo={false}
              clearOnEscape
              clearOnBlur
            />
          </FormControl>

          <Button
            variant="contained"
            onClick={handleAdd}
            startIcon={<Plus />}
            disabled={!selectedSMA}
          >
            Add SMA
          </Button>

          {selectedSMAs.length > 0 && (
            <>
              <Typography variant="subtitle2">Selected SMAs:</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {selectedSMAs.map((sma) => {
                  const name = smaOptions.find((option) => option.smaNumber === sma.value)?.name;
                  const label = name ? `${name} - ${sma.value}` : sma.value;
                  return (
                    <Chip
                      key={sma.value}
                      label={label}
                      onDelete={() => onRemoveSelectedSMA(sma)}
                      color="primary"
                      data-testid={`sma-chip-${sma.value}`}
                      sx={{
                        fontWeight: 600,
                      }}
                    />
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={handleCancel} color="secondary" variant="outlined">
          CANCEL
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={selectedSMAs.length === 0}
        >
          ADD SMAs
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSMADialog;
